import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { devicesApi } from '../../../services/apiClient';
import LocationService from '../../../services/locationService';
import { getLocationId, getLocationName } from '../utils/locationTransformers';

/**
 * Hook for managing all dialog state in Location Management
 * Handles location CRUD dialogs, device dialogs, and button test dialog
 */
export const useLocationDialogs = ({ selectedLocation, setSelectedLocation, selectLocation, refresh, t }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Location dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [parentLocation, setParentLocation] = useState(null);

  // Delete location
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);

  // Device dialog
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [deviceDialogMode, setDeviceDialogMode] = useState('create');
  const [deviceToEdit, setDeviceToEdit] = useState(null);

  // Delete device
  const [deleteDeviceConfirmOpen, setDeleteDeviceConfirmOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  // Button test dialog
  const [buttonTestDialogOpen, setButtonTestDialogOpen] = useState(false);

  // --- Location CRUD ---

  const handleAddLocation = useCallback((parent) => {
    setParentLocation(parent);
    setDialogMode('create');
    setDialogOpen(true);
  }, []);

  const handleEditLocation = useCallback((location) => {
    setSelectedLocation(location);
    setDialogMode('edit');
    setDialogOpen(true);
  }, [setSelectedLocation]);

  const handleDeleteLocation = useCallback((location) => {
    setLocationToDelete(location);
    setConfirmDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!locationToDelete) return;

    const name = getLocationName(locationToDelete);
    try {
      await LocationService.deleteLocation(getLocationId(locationToDelete));
      enqueueSnackbar(
        t ? t('LocationManagement.location_deleted', { name }) : `"${name}" deleted successfully`,
        { variant: 'success' }
      );
      refresh();
      setConfirmDialogOpen(false);
      setLocationToDelete(null);
      if (selectedLocation && getLocationId(selectedLocation) === getLocationId(locationToDelete)) {
        setSelectedLocation(null);
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      enqueueSnackbar(
        t ? t('LocationManagement.error_deleting_location') : `Failed to delete "${name}"`,
        { variant: 'error' }
      );
    }
  }, [locationToDelete, selectedLocation, setSelectedLocation, refresh, enqueueSnackbar, t]);

  const handleSave = useCallback(() => {
    enqueueSnackbar(
      dialogMode === 'create'
        ? (t ? t('LocationManagement.location_created') : 'Location created successfully')
        : (t ? t('LocationManagement.location_updated') : 'Location updated successfully'),
      { variant: 'success' }
    );
    refresh();
    setDialogOpen(false);
  }, [refresh, dialogMode, enqueueSnackbar, t]);

  // --- Device CRUD ---

  const handleAddDevice = useCallback(() => {
    setDeviceToEdit(null);
    setDeviceDialogMode('create');
    setDeviceDialogOpen(true);
  }, []);

  const handleEditDevice = useCallback((device) => {
    setDeviceToEdit(device);
    setDeviceDialogMode('edit');
    setDeviceDialogOpen(true);
  }, []);

  const handleDeleteDevice = useCallback((device) => {
    setDeviceToDelete(device);
    setDeleteDeviceConfirmOpen(true);
  }, []);

  const handleDeviceSave = useCallback(async () => {
    enqueueSnackbar(
      deviceDialogMode === 'create'
        ? (t ? t('LocationManagement.device_added') : 'Device added successfully')
        : (t ? t('LocationManagement.device_updated') : 'Device updated successfully'),
      { variant: 'success' }
    );
    setDeviceDialogOpen(false);
    if (selectedLocation) {
      await selectLocation(selectedLocation);
    }
  }, [selectedLocation, selectLocation, deviceDialogMode, enqueueSnackbar, t]);

  const handleConfirmDeviceDelete = useCallback(async () => {
    if (deviceToDelete) {
      const deviceName = deviceToDelete.friendlyName || 'Device';
      try {
        const deviceId = deviceToDelete.id || deviceToDelete._id || deviceToDelete.device_UUID;
        await devicesApi.delete(deviceId);
        enqueueSnackbar(
          t ? t('LocationManagement.device_deleted', { name: deviceName }) : `"${deviceName}" deleted successfully`,
          { variant: 'success' }
        );
        setDeleteDeviceConfirmOpen(false);
        setDeviceToDelete(null);
        if (selectedLocation) {
          await selectLocation(selectedLocation);
        }
      } catch (error) {
        console.error('Error deleting device:', error);
        enqueueSnackbar(
          t ? t('LocationManagement.error_deleting_device') : `Failed to delete "${deviceName}"`,
          { variant: 'error' }
        );
      }
    }
  }, [deviceToDelete, selectedLocation, selectLocation, enqueueSnackbar, t]);

  return {
    // Location dialog
    dialogOpen,
    setDialogOpen,
    dialogMode,
    parentLocation,
    handleAddLocation,
    handleEditLocation,
    handleDeleteLocation,
    confirmDialogOpen,
    setConfirmDialogOpen,
    locationToDelete,
    setLocationToDelete,
    confirmDelete,
    handleSave,

    // Device dialog
    deviceDialogOpen,
    setDeviceDialogOpen,
    deviceDialogMode,
    deviceToEdit,
    handleAddDevice,
    handleEditDevice,
    handleDeleteDevice,
    handleDeviceSave,
    deleteDeviceConfirmOpen,
    setDeleteDeviceConfirmOpen,
    deviceToDelete,
    handleConfirmDeviceDelete,

    // Button test
    buttonTestDialogOpen,
    setButtonTestDialogOpen
  };
};

export default useLocationDialogs;
